/**
 * primary-pdp.js — OMI Well Beauty
 *
 * Extracted from inline <script> in the primary-pdp section block.
 * Load via: {{ 'primary-pdp.js' | asset_url | script_tag }}
 * or in theme.liquid before </body>.
 *
 * ─────────────────────────────────────────────────────────────────
 * ORIGINAL LOGIC — 100% preserved, zero behaviour changes:
 *   - Swatch click → filter tabs by variant title
 *   - Swatch click → filter galleries by variant name
 *   - Swatch click → sync all matching swatches page-wide
 *   - Capsules-specific Glide remount
 *   - Sticky CTA chip filtering
 *   - Swatch → global-variant-change-trigger sync (capture listener)
 *
 * ACCESSIBILITY ADDITIONS — WCAG 2.2 AA (Slade v. OMI remediation)
 *   Section 8 (PDP variant selector) + Section 9 (Subscription options)
 *
 *   A11Y A  Arrow key navigation inside .products[role="radiogroup"]
 *           ArrowDown/ArrowRight → next option
 *           ArrowUp/ArrowLeft   → previous option (wraps)
 *           Home                → first option
 *           End                 → last option
 *           Enter / Space       → activate focused option
 *           WCAG 2.1.1 Keyboard.
 *
 *   A11Y B  Roving tabindex sync on Arrow key move
 *           Only the focused option holds tabindex="0"; all others
 *           get tabindex="-1". Tab exits the group; Arrow keys
 *           navigate within it. Standard ARIA radiogroup pattern.
 *           WCAG 2.1.1 Keyboard.
 *
 *   A11Y C  aria-checked sync on click
 *           When a tab is activated via click (or via the existing
 *           swatch→trigger sync), aria-checked is set to "true" on
 *           the newly selected tab and "false" on all others.
 *           WCAG 4.1.2 Name, Role, Value.
 *
 *   A11Y D  aria-live announcement on selection
 *           Writes "[option label] selected." into the visually-hidden
 *           aria-live="polite" region inside the radiogroup.
 *           AT announces the selection without any focus movement.
 *           Addresses complaint §34: "no notification when a
 *           subscription option is selected."
 *           WCAG 4.1.3 Status Messages.
 *
 *   A11Y E  filterTabsByVariantTitle now preserves roving tabindex
 *           When tabs are shown/hidden by swatch selection, the
 *           tabindex and aria-checked states are resynced so the
 *           first visible selected tab always holds tabindex="0".
 * ─────────────────────────────────────────────────────────────────
 */

document.addEventListener('DOMContentLoaded', () => {
  const pdpElement = document.querySelector('.primary-pdp');
  if (!pdpElement) return;

  const productTabs    = pdpElement.querySelectorAll('.product-tab');
  const swatchWrapper  = document.querySelector('.pdp-variant-swatch-selector');
  const swatchButtons  = document.querySelectorAll('.pdp-variant-swatch-btn');

  const galleries         = document.querySelectorAll('.product .gallery-wpr media-gallery[data-filtered-by-variant-name]');
  const fallbackGalleries = document.querySelectorAll('.product .gallery-wpr media-gallery:not([data-filtered-by-variant-name])');

  // ─── Helpers (unchanged) ──────────────────────────────────────

  const norm = (v) => (v ?? '').toString().trim().toLowerCase();
  const getSwatchValue = (btn) => btn?.value || btn?.dataset?.value || btn?.textContent?.trim() || '';

  function parseOptions(el) {
    const raw = el.getAttribute('data-options') || el.dataset.options || '';
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      try {
        return JSON.parse(raw.replace(/&quot;/g, '"').replace(/&#39;/g, "'"));
      } catch {
        console.warn('Invalid data-options JSON on element:', el, raw);
        return null;
      }
    }
  }

  // ─── A11Y helpers ─────────────────────────────────────────────

  /**
   * A11Y C + A11Y B
   * Sync aria-checked and roving tabindex across all radio tabs
   * in the same radiogroup as the activated tab.
   */
  function syncRadioState(activatedTab) {
    const group = activatedTab.closest('[role="radiogroup"]');
    if (!group) return;

    const allTabs = Array.from(group.querySelectorAll('button.product-tab'));
    allTabs.forEach((tab) => {
      const isActive = tab === activatedTab;
      tab.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });
  }

  /**
   * A11Y D
   * Write a plain-English announcement into the group's aria-live region.
   * The brief textContent reset before the rAF ensures AT re-announces
   * even if the same option is selected twice in a row.
   */
  function announceSelection(activatedTab) {
    const group = activatedTab.closest('[role="radiogroup"]');
    if (!group) return;

    const region = group.querySelector('[aria-live="polite"]');
    if (!region) return;

    const label = activatedTab.getAttribute('aria-label') || activatedTab.textContent.trim();
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = label + ' selected.';
    });
  }

  // ─── filterTabsByVariantTitle (original + A11Y E) ─────────────

  function filterTabsByVariantTitle(keyword) {
    const target = norm(keyword);

    productTabs.forEach((tab) => {
      const opts = parseOptions(tab);
      const vTitle = norm(opts?.variant_title || '');
      tab.style.display = vTitle.includes(target) ? '' : 'none';
    });

    // A11Y E — after hiding/showing tabs, ensure aria-checked is still
    // accurate for the visible set. With native buttons, tabindex is
    // managed by the browser automatically — no roving tabindex needed.
    const group = pdpElement.querySelector('[role="radiogroup"]');
    if (!group) return;

    const visibleTabs = Array.from(
      group.querySelectorAll('button.product-tab')
    ).filter((t) => t.style.display !== 'none');

    // If no visible tab is checked, check the first visible one
    const anyChecked = visibleTabs.some((t) => t.getAttribute('aria-checked') === 'true');
    if (!anyChecked && visibleTabs[0]) {
      visibleTabs[0].setAttribute('aria-checked', 'true');
    }
  }

  // ─── filterGalleriesByVariantName (unchanged) ─────────────────

  function filterGalleriesByVariantName(keyword) {
    const target = norm(keyword);
    let anyMatched = false;

    galleries.forEach((gal) => {
      const raw    = gal.dataset.filteredByVariantName || '';
      const values = raw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      const match  = values.includes(target);
      gal.style.display = match ? '' : 'none';
      gal.classList.toggle('is-active', match);
      if (match) anyMatched = true;
    });

    fallbackGalleries.forEach((gal) => {
      gal.style.display = anyMatched ? 'none' : '';
      gal.classList.toggle('is-active', !anyMatched);
    });
  }

  // ─── activateSwatchesByValue (unchanged) ──────────────────────

  function activateSwatchesByValue(value) {
    const needle = norm(value);
    document.querySelectorAll('.pdp-variant-swatch-btn').forEach((btn) => {
      const val = norm(getSwatchValue(btn));
      btn.classList.toggle('active', val === needle);
    });
  }

  // ─── Initial run (unchanged) ──────────────────────────────────

  const initialKeyword = swatchWrapper?.dataset?.activeValue || '';
  if (initialKeyword) {
    activateSwatchesByValue(initialKeyword);
    filterTabsByVariantTitle(initialKeyword);
    filterGalleriesByVariantName(initialKeyword);
  }

  // ─── Swatch click handlers (unchanged) ────────────────────────

  swatchButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const keyword  = getSwatchValue(button);
      const isSynced = button.dataset.syncedClick === '1';

      activateSwatchesByValue(keyword);

      if (keyword) {
        if (swatchWrapper) swatchWrapper.dataset.activeValue = keyword;
        filterTabsByVariantTitle(keyword);
        filterGalleriesByVariantName(keyword);
      }

      if (!isSynced) {
        const needle = norm(keyword);
        document.querySelectorAll('.pdp-variant-swatch-btn[data-value], .pdp-variant-swatch-btn').forEach((el) => {
          if (el === button) return;
          if (el.matches('[disabled], [aria-disabled="true"]')) return;
          const val = norm(getSwatchValue(el));
          if (val === needle) {
            el.dataset.syncedClick = '1';
            el.click();
          }
        });
      }

      if (isSynced) button.dataset.syncedClick = '';
    });
  });

  // ─── Capsules Glide remount (unchanged) ───────────────────────

  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('button.pdp-variant-swatch-btn');
    if (!btn) return;
    if (btn.dataset.syncedClick === '1') return;
    if (getSwatchValue(btn) !== 'Capsules') return;

    const sliderContainer = document.querySelector('[data-filtered-by-variant-name="Capsules"] .glide');
    if (!sliderContainer || typeof Glide === 'undefined') return;

    if (sliderContainer.__glideInstance) {
      try { sliderContainer.__glideInstance.destroy(); } catch (e) {}
    }

    const glideInstance = new Glide(sliderContainer, {
      type: 'slider',
      perView: 1,
      startAt: 0,
      gap: 0,
      peek: { before: 0, after: 50 },
    });

    glideInstance.mount();
    sliderContainer.__glideInstance = glideInstance;
  });

  // ─── Sticky CTA chip filtering (unchanged) ────────────────────

  document.addEventListener('click', (e) => {
    const chip = e.target.closest('.sticky-cta-option-value');
    if (!chip) return;

    const section = chip.closest('.shopify-section');
    if (!section) return;

    const needle = norm(chip.dataset.value || '');
    section.querySelectorAll('.sticky-cta-option-value').forEach((el) => el.classList.remove('active'));
    chip.classList.add('active');

    section.querySelectorAll('.custom-variant-dropdown__option').forEach((opt) => {
      const title = norm(opt.dataset.title || '');
      opt.style.display = title.includes(needle) ? 'flex' : 'none';
    });
  });

  // ─── A11Y A: Arrow key navigation (optional convenience) ────────
  // Buttons are natively Tab-reachable so this is a progressive
  // enhancement. Arrow keys let power users move without Tab-cycling
  // through every interactive element on the page.
  // Enter/Space are already handled natively by <button>.

  document.querySelectorAll('[role="radiogroup"]').forEach((group) => {

    group.addEventListener('keydown', (e) => {
      const focused = document.activeElement;
      if (!focused || !group.contains(focused)) return;
      if (!focused.matches('button.product-tab')) return;

      // Visible tabs only — hidden tabs (filtered by swatch) are skipped
      const tabs = Array.from(
        group.querySelectorAll('button.product-tab')
      ).filter((t) => t.style.display !== 'none');

      const currentIndex = tabs.indexOf(focused);
      if (currentIndex === -1) return;

      let nextIndex = null;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        tabs[nextIndex].focus();
        // Arrow key moves focus only — selection happens on click/Enter/Space
      }
    });

    // A11Y C + A11Y D — sync aria-checked and announce on click
    group.addEventListener('click', (e) => {
      const tab = e.target.closest('button.product-tab');
      if (!tab) return;
      syncRadioState(tab);
      announceSelection(tab);

      const isOtp = tab.classList.contains('one-time-purchase');
      const atcBtn = pdpElement.querySelector('button.add-to-cart-btn');

      // ── CTA TEXT — Subscribe / Add to bag ──────────────────────
      // Mirrors the Liquid default set at render time.
      // Gated by section setting `enable_cta_label_switching` — when
      // disabled, the label rendered by Liquid stays put across variant
      // changes. Default attribute value is "true" (Shopify checkbox
      // serialises booleans as "true"/"false" strings).
      // parseOptions() is already defined above in the same closure.
      const productInfo = pdpElement.closest('product-info') || document.querySelector('product-info');
      const ctaLabelSwitchingEnabled = productInfo?.dataset.ctaLabelSwitching !== 'false';

      if (ctaLabelSwitchingEnabled) {
        const opts = parseOptions(tab);
        const hasSellingPlan = opts?.selling_plan_id && opts.selling_plan_id !== '' && opts.selling_plan_id !== 'null';
        const textEl = pdpElement.querySelector('button.add-to-cart-btn .text');
        if (textEl) {
          textEl.textContent = hasSellingPlan ? 'Subscribe' : 'Add to bag';
        }
      }
      // ───────────────────────────────────────────────────────────

      // A11Y F support — clear strikethrough on OTP so the observer
      // omits the "originally $X" clause in the aria-label.
      if (isOtp && atcBtn) {
        atcBtn.setAttribute('data-price_strikethrough', '');
      }
    });

  });

  // ─── A11Y F: Add-to-cart button aria-label sync ───────────────
  // The complaint cites AT reading "$474 $378" with no distinction
  // between original and sale price. The Liquid sets a correct
  // initial aria-label. This observer watches for the theme JS
  // updating data-current_price / data-price_strikethrough on the
  // button (which happens on every variant tab click) and rewrites
  // aria-label to match, without requiring changes to the theme JS.
  // Format: "[Submit text], sale price $X, originally $Y"
  // If no strikethrough: "[Submit text] $X"
  // WCAG 1.3.1 Info and Relationships.

  const atcBtn = pdpElement.querySelector('button.add-to-cart-btn');
  if (atcBtn) {
    function updateAtcAriaLabel() {
      const submitText    = atcBtn.querySelector('.text')?.textContent?.trim() || '';
      const currentPrice  = atcBtn.getAttribute('data-current_price') || '';
      const strikethrough = atcBtn.getAttribute('data-price_strikethrough') || '';

      // data-current_price can be either:
      //   - Raw cents integer (set by Liquid): 19800  → $198
      //   - Pre-formatted string (set by theme JS): "$198" or "$198.00"
      // We normalise both to a dollar amount, then reformat consistently.
      function parseToDollars(value) {
        if (!value) return null;
        const str = String(value).replace(/[^0-9.]/g, ''); // strip $ and commas
        if (!str) return null;
        const n = parseFloat(str);
        if (isNaN(n)) return null;
        // If the value is suspiciously large it's in cents — divide by 100
        // Threshold: anything over 1000 is almost certainly cents
        return n > 1000 ? n / 100 : n;
      }

      function formatMoney(dollars) {
        if (dollars === null) return '';
        return '$' + (Number.isInteger(dollars) ? dollars : dollars.toFixed(2).replace(/\.00$/, ''));
      }

      const currentDollars  = parseToDollars(currentPrice);
      const strikeDollars   = parseToDollars(strikethrough);
      const hasDiscount     = strikeDollars !== null && currentDollars !== null && strikeDollars > currentDollars;

      const current = formatMoney(currentDollars);
      const strike  = formatMoney(strikeDollars);

      // const label = hasDiscount
      //   ? `${submitText}, sale price ${current}, originally ${strike}`
      //   : `${submitText} ${current}`;
      const label = hasDiscount
        ? `${submitText} ${current}, originally ${strike}`
        : `${submitText} ${current}`;

      atcBtn.setAttribute('aria-label', label.trim());
    }

    // Watch for attribute changes the theme JS makes on variant switch.
    // Both data-current_price and data-price_strikethrough update together,
    // but not always in the same microtask. We debounce via rAF so we always
    // read both attributes in their final state — this prevents the "originally"
    // clause from persisting when switching to the OTP variant which has no
    // strikethrough price.
    let rafPending = false;
    new MutationObserver(() => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        updateAtcAriaLabel();
      });
    }).observe(atcBtn, { attributes: true, attributeFilter: ['data-current_price', 'data-price_strikethrough'] });

    // Also re-run when .text content changes (submit_type toggle: Subscribe ↔ Buy Once)
    const textEl = atcBtn.querySelector('.text');
    if (textEl) {
      new MutationObserver(() => updateAtcAriaLabel())
        .observe(textEl, { childList: true, characterData: true, subtree: true });
    }
  }

  // ─── Focus guard ──────────────────────────────────────────────────────────────
  // Problem: the theme's global .global-variant-change-trigger click handler
  // moves focus to the sticky ATC bar after every variant selection. Because
  // .product-tab also carries that class, clicking any tab triggers the
  // redirect, pulling keyboard users away from where they were.
  //
  // Fix: capture the active element BEFORE the event propagates to the theme
  // handler (capture phase), then schedule a focus restore via rAF — which runs
  // after all synchronous click handlers and any resulting .focus() calls have
  // finished. Only applies to tabs inside .primary-pdp; all other triggers on
  // the page are unaffected.
  //
  // WCAG 2.1.1 Keyboard / 2.4.3 Focus Order.

  pdpElement.querySelectorAll('button.product-tab.global-variant-change-trigger').forEach((tab) => {
    tab.addEventListener(
      'click',
      (e) => {
        const origin = e.currentTarget; // the tab that was clicked

        requestAnimationFrame(() => {
          // Only restore if focus has moved away from this tab.
          // If the theme didn't steal focus, this is a no-op.
          if (document.activeElement !== origin) {
            origin.focus({ preventScroll: true });
          }
        });
      },
      true // capture phase — runs before the theme's bubble-phase handler
    );
  });


});

// ─── Swatch → global-variant-change-trigger sync ──────────────
// Kept outside DOMContentLoaded intentionally (capture phase,
// must run before stopPropagation in child handlers).
// Logic unchanged from original.

document.addEventListener('click', (e) => {
  const swatch = e.target.closest('.pdp-variant-swatch-btn');
  if (!swatch) return;

  document
    .querySelectorAll('.global-variant-change-trigger[data-preferred-option-name-selection]')
    .forEach((el) => {
      const styleAttr   = el.getAttribute('style') || '';
      const inlineHidden = /(^|;)\s*display\s*:\s*none\s*(;|$)/i.test(styleAttr);
      if (inlineHidden) return;
      if (el.matches('[disabled], [aria-disabled="true"]')) return;
      if (el.dataset.syncedClick === '1') { el.dataset.syncedClick = ''; return; }
      el.dataset.syncedClick = '1';
      el.click();
    });
}, true); // capture phase — unchanged